import Vue from 'vue'
import VueRouter from 'vue-router'
import { clearAuthState, shouldAttemptSessionRestore } from '@/utils/userSession'
import { getAccessToken } from '@/utils/accessToken'
import { ensureAccessToken, wasLastRefreshRejected } from '@/api/authRefresh'

import MainPage from '../views/MainPage.vue'
import HomePage from '../views/HomePage.vue'
import SearchPage from '../views/SearchPage.vue'
import WeekReport from '../views/WeekReport.vue'
import WeekReportMbti from '../views/WeekReportMbti.vue'
import Profile from '../views/Profile.vue'
import ProfileInfo from '../views/ProfileInfo.vue'
import ProfileTaskList from '../views/ProfileTaskList.vue'
import ChangePassword from '../views/ChangePassword.vue'
import Login from '../views/Login.vue'
import Register from '../views/Register.vue'
import UserAgreement from '../views/UserAgreement.vue'
import PrivacyPolicy from '../views/PrivacyPolicy.vue'
import SummitHome from '../views/SummitHome.vue'
import SummitDetail from '../views/SummitDetail.vue'
import SummaryListPage from '../views/SummaryListPage.vue'
import MyNotesPage from '../views/MyNotesPage.vue'
import NoteCardStack from '../views/NoteCardStack.vue'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: '登录', requiresAuth: false }
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { title: '注册', requiresAuth: false }
  },
  {
    path: '/user-agreement',
    name: 'UserAgreement',
    component: UserAgreement,
    meta: { title: '用户协议', requiresAuth: false }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { title: '隐私政策', requiresAuth: false }
  },
  {
    path: '/change-password',
    name: 'ChangePasswordPublic',
    component: ChangePassword,
    meta: { title: '忘记密码', requiresAuth: false, publicChangePassword: true }
  },
  {
    path: '/',
    component: MainPage,
    redirect: '/summary-list',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'home',
        name: 'HomePage',
        component: HomePage,
        meta: { title: '首页', keepAlive: true }
      },
      {
        path: 'search',
        name: 'SearchPage',
        component: SearchPage,
        meta: { title: '搜索', keepAlive: true }
      },
      {
        path: 'summary-list',
        name: 'SummaryListPage',
        component: SummaryListPage,
        meta: { title: '内容列表', keepAlive: true }
      },
      {
        path: 'summary-list/detail',
        name: 'SummaryListDetail',
        component: SummitDetail,
        meta: { title: '总结详情' }
      },
      {
        path: 'summary-list/notes',
        name: 'MyNotesPage',
        component: MyNotesPage,
        meta: { title: '我的笔记', keepAlive: true }
      },
      {
        path: 'note-cards',
        name: 'NoteCardStack',
        component: NoteCardStack,
        meta: { title: '笔记卡片', keepAlive: false }
      },
      {
        path: 'summit',
        name: 'SummitHome',
        component: SummitHome,
        meta: { title: '总结首页', keepAlive: true }
      },
      {
        path: 'extract',
        redirect: '/summit'
      },
      {
        path: 'summit/detail',
        name: 'SummitDetail',
        component: SummitDetail,
        meta: { title: '总结详情' }
      },
      {
        path: 'week-report',
        redirect: '/profile/week-report'
      },
      {
        path: 'profile/week-report',
        name: 'WeekReport',
        component: WeekReport,
        meta: { title: '周报', keepAlive: true }
      },
      {
        path: 'profile/week-report/mbti',
        name: 'WeekReportMbti',
        component: WeekReportMbti,
        meta: { title: '内容偏好人格', keepAlive: false }
      },
      {
        path: 'profile',
        name: 'ProfilePage',
        component: Profile,
        meta: { title: '我的', keepAlive: true }
      },
      {
        path: 'profile/change-password',
        name: 'ChangePassword',
        component: ChangePassword,
        meta: { title: '修改密码', keepAlive: false }
      },
      {
        path: 'profile/info',
        name: 'ProfileInfo',
        component: ProfileInfo,
        meta: { title: '个人资料', keepAlive: false }
      },
      {
        path: 'profile/tasks',
        name: 'ProfileTaskList',
        component: ProfileTaskList,
        meta: { title: '分析任务', keepAlive: false }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

function routeRequiresAuth(to) {
  return to.matched.some((record) => record.meta && record.meta.requiresAuth === true)
}

router.beforeEach(async (to, from, next) => {
  const authRequired = routeRequiresAuth(to)

  if (!authRequired) {
    if ((to.path === '/login' || to.path === '/register') && getAccessToken()) {
      next('/')
    } else {
      next()
    }
    return
  }

  if (getAccessToken()) {
    next()
    return
  }

  if (shouldAttemptSessionRestore()) {
    const recovered = await ensureAccessToken()
    if (recovered) {
      next()
      return
    }
  }

  if (wasLastRefreshRejected()) {
    clearAuthState()
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  // 网络、超时或服务端临时故障时保留 rememberMe / loggedIn，
  // 用户刷新页面后仍会继续尝试从 HttpOnly Cookie 恢复会话。
  next({
    path: '/login',
    query: { redirect: to.fullPath, sessionRestore: 'retry' }
  })
})

export default router